// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import {
  selectClient,
  selectClientId,
  selectClientInstitutions,
} from '@shared/data-access/slices/clients/clients-selectors';
import { selectAccountCurrency } from '@shared/data-access/slices/user';
import { advisoryApi } from '@shared/data-access/api/brokerage/advisory-api.v2';
import { type AdvisoryInstitution } from '@shared/data-access/types/brokerage-types';
import { createDataProvider } from '@shared/ui-kit/providers';
import { appSelect, isAdvisorApp } from '@shared/ui-kit/utils/app-utils';
import { isDefined } from '@shared/utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

export const useInstitutionFactory = () => {
  const { t } = useTranslation();

  const clientId = useSelector(selectClientId);

  const client = useSelector(selectClient);

  const institutions = useSelector(selectClientInstitutions);

  const selectedCurrency = useSelector(selectAccountCurrency);

  const syncClientId = appSelect({
    advisor: clientId ?? undefined,
    client: undefined,
  });

  const institutionsQueryArgs = useMemo(
    () => ({
      currency: selectedCurrency,
      ...(isDefined(syncClientId) ? { client_id: syncClientId } : {}),
    }),
    [selectedCurrency, syncClientId],
  );

  // Aggregation reads institutions from the clients slice; ensure they are
  // fetched here so /aggregation works without visiting /banks first.
  advisoryApi.useGetAdvisoryInstitutionsQuery(institutionsQueryArgs, {
    skip: isAdvisorApp && !isDefined(syncClientId),
  });

  const accounts = useMemo(() => {
    if (!client) {
      return [];
    }

    const clientInstitutions =
      institutions.length > 0
        ? [
            {
              id: 'ALL',
              institution_id: 'ALL',
              name: t('common.all', {
                ns: 'common',
                context: 'female',
              }) as string,
              provider: 'citec',
              products: {
                accounts: [],
                cards: [],
                loans: [],
                portfolios: [],
              },
              error: null,
              last_sync: null,
              logo_url: null,
            } as AdvisoryInstitution,
            ...institutions,
          ]
        : [];

    return [
      {
        ...client,
        institutions: clientInstitutions,
      },
    ];
  }, [client, institutions, t]);

  return {
    clients: accounts,
    clientId: clientId ?? undefined,
    client,
  };
};

export const {
  useInstitutionData,
  InstitutionProvider,
  InstitutionConsumer,
  InstitutionDataContext,
  withInstitution,
} = createDataProvider({
  name: 'Institution',
  factory: useInstitutionFactory,
});
